import React from 'react';
import { Alert, Button, Card, Progress, Space, Table, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { ERROR_TYPES, PRIMARY_COLOR } from '../upload.constants';

const { Text, Title } = Typography;

interface FileProcessingProps {
  isParsing: boolean;
  parsingProgress: number;
  parsedData: any[];
  parsedHeaders: string[];
  selectedOperation: any;
  errors: any[];
  errorMap: any;
  editingCell: { rowIndex: number; field: string } | null;
  setEditingCell: (cell: { rowIndex: number; field: string } | null) => void;
  handleCellEdit: (rowIndex: number, field: string, value: string) => void;
  exportToExcel: () => void;
  pageSize: number;
  setPageSize: (size: number) => void;
}

const FileProcessing: React.FC<FileProcessingProps> = ({
  isParsing,
  parsingProgress,
  parsedData,
  parsedHeaders,
  selectedOperation,
  errors,
  errorMap,
  editingCell,
  setEditingCell,
  handleCellEdit,
  exportToExcel,
  pageSize,
  setPageSize,
}) => {
  const getCellStyle = (record: any, columnKey: string) => {
    const lineNumber = parsedData.findIndex(row => row === record);
    if (lineNumber === -1) return {};

    const actualLineNumber = lineNumber + 1;
    if (errorMap[actualLineNumber] && errorMap[actualLineNumber][columnKey]) {
      const errorType = errorMap[actualLineNumber][columnKey];
      const backgroundColor = `#${ERROR_TYPES[errorType]?.color?.slice(2) || '#ff4d4f'}`;
      return {
        backgroundColor,
        padding: errorType === 'MISSING_FIELD' ? 10 : 10,
      };
    }
    return {};
  };

  const getRowStyle = (record: any) => {
    const lineNumber = parsedData.findIndex(row => row === record) + 1;
    if (errorMap[lineNumber]) {
      const hasSpecificCellErrors = Object.keys(errorMap[lineNumber]).length > 0;
      if (!hasSpecificCellErrors) {
        return {
          backgroundColor: `#${ERROR_TYPES.MISSING_FIELD.color.slice}`,
        };
      }
    }
    return {};
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Title level={4}>Validation des données</Title>
      {isParsing ? (
        <Card style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>Analyse du fichier en cours...</Text>
            <Progress percent={parsingProgress} status="active" />
          </Space>
        </Card>
      ) : parsedData.length > 0 ? (
        <>
          {errors.some(err => err.type === 'INVALID_HEADER') ? (
            <Alert
              message="Erreur d'en-tête"
              description={
                <div>
                  <Text>
                    Les en-têtes du fichier ne correspondent pas au modèle attendu. Veuillez vérifier que les colonnes du fichier Excel
                    correspondent exactement au modèle suivant : <strong>{selectedOperation?.schema.join(', ')}</strong>.
                  </Text>
                  <br />
                  <Text>
                    Téléchargez le modèle correct ici :{' '}
                    <a href={selectedOperation.templateUrl} download>
                      Télécharger le template
                    </a>
                  </Text>
                </div>
              }
              type="error"
              showIcon
              style={{ marginBottom: 16, borderRadius: '4px' }}
            />
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <Space>
                  <Alert message={`${parsedData.length} lignes analysées`} type="info" showIcon style={{ borderRadius: '4px' }} />
                </Space>
                <Button
                  type="primary"
                  onClick={exportToExcel}
                  style={{
                    backgroundColor: '#52c41a',
                    borderColor: '#52c41a',
                    borderRadius: '4px',
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    fontWeight: '500',
                  }}
                >
                  <DownloadOutlined style={{ fontSize: 16 }} />
                  Exporter vers Excel
                </Button>
              </div>

              {errors && errors.length > 0 && (
                <>
                  <Card style={{ marginBottom: 16 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text strong>Instructions de correction :</Text>
                      <Text type="secondary">
                        Cliquez directement sur les cellules colorées pour les corriger. Les modifications sont validées en temps réel.
                      </Text>
                    </Space>
                  </Card>

                  <div className="error-legend">
                    {Object.entries(ERROR_TYPES).map(([key, { color, label }]) => (
                      <div key={key} className="legend-item">
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            backgroundColor: `#${color.slice(2)}`,
                            marginRight: 8,
                            borderRadius: '4px',
                          }}
                        />
                        <Text>{label}</Text>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <Table
                className="validation-table"
                dataSource={parsedData}
                columns={selectedOperation?.schema.map(field => ({
                  title: field,
                  dataIndex: field,
                  key: field,
                  render(text, record, rowIndex) {
                    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.field === field;
                    const hasError = errorMap[rowIndex + 1]?.[field];
                    const errorType = hasError ? errorMap[rowIndex + 1][field] : null;

                    return (
                      <div
                        style={{
                          position: 'relative',
                          cursor: hasError ? 'pointer' : 'default',
                          padding: '8px',
                          borderRadius: '4px',
                          ...getCellStyle(record, field),
                        }}
                        onClick={() => hasError && setEditingCell({ rowIndex, field })}
                        title={hasError ? `Cliquez pour corriger: ${ERROR_TYPES[errorType]?.label || errorType}` : undefined}
                      >
                        {isEditing ? (
                          <input
                            value={text}
                            onChange={e => handleCellEdit(rowIndex, field, e.target.value)}
                            onKeyPress={e => e.key === 'Enter' && setEditingCell(null)}
                            onBlur={() => setEditingCell(null)}
                            autoFocus
                            style={{
                              width: '100%',
                              backgroundColor: 'white',
                              border: `1px solid ${errorType ? '#ff4d4f' : '#d9d9d9'}`,
                            }}
                          />
                        ) : (
                          <span>{text}</span>
                        )}
                      </div>
                    );
                  },
                }))}
                onRow={record => ({
                  style: getRowStyle(record),
                })}
                pagination={{
                  pageSize,
                  onChange: (_, pagesize) => setPageSize(pagesize),
                }}
                scroll={{ x: true }}
                style={{ marginBottom: 16 }}
              />

              {errors.length > 0 && (
                <Alert
                  message={`${errors.length} erreur(s) trouvée(s)`}
                  description={
                    <Table
                      dataSource={errors}
                      columns={[
                        {
                          title: 'Ligne',
                          dataIndex: 'line',
                          key: 'line',
                          width: 100,
                          render(line) {
                            return <>{line + 1}</>;
                          },
                        },
                        { title: 'Erreurs', dataIndex: 'errors', key: 'errors' },
                        {
                          title: 'Type',
                          dataIndex: 'type',
                          key: 'type',
                          render: type => (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <div
                                style={{
                                  width: 12,
                                  height: 12,
                                  backgroundColor: `#${ERROR_TYPES[type]?.color.slice(2)}`,
                                  marginRight: 8,
                                  borderRadius: '4px',
                                }}
                              />
                              <span>{ERROR_TYPES[type]?.label || type}</span>
                            </div>
                          ),
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                  }
                  type="error"
                  showIcon
                  style={{ borderRadius: '4px' }}
                />
              )}
            </>
          )}
        </>
      ) : (
        <Alert
          message="Aucun fichier chargé"
          description="Veuillez retourner à l'étape précédente et charger un fichier Excel."
          type="warning"
          showIcon
          style={{ borderRadius: '4px' }}
        />
      )}
    </div>
  );
};

export default FileProcessing;
